import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, map, startWith } from 'rxjs';
import { IMenuItem } from '../side-menu/side-menu.component';

@Component({
    selector: 'pims-menu-item',
    imports: [CommonModule, TranslatePipe, MatMenuModule, MatListModule, RouterModule],
    templateUrl: './menu-item.component.html',
    styleUrl: './menu-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuItemComponent {
    private readonly router = inject(Router);

    readonly collapsed = input(false);
    readonly item = input.required<IMenuItem>();
    readonly level = input.required<number>();
    readonly nestedMenuOpen = signal(false);

    private readonly currentUrl = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map(() => this.router.url.split('?')[0]),
            startWith(this.router.url.split('?')[0]),
        ),
    );

    constructor() {
        effect(() => {
            if (this.collapsed()) {
                this.nestedMenuOpen.set(false);
            }
        });
    }

    itemClasses(item: IMenuItem): Record<string, boolean> {
        const active = this.isActive(item);
        return {
            active: active,
            active0: this.level() === 0 && active && !!item.subItems,
            'side-nav__item--disabled': this.isDisabled(item),
        };
    }

    isDisabled(item: IMenuItem): boolean {
        return !!item.disabled;
    }

    onItemClick(event: Event, item: IMenuItem): void {
        if (this.isDisabled(item)) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        if (item.subItems) {
            this.toggleNestedMenu(event);
        }
    }

    onSubItemClick(event: Event, item: IMenuItem): void {
        if (this.isDisabled(item)) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    isActive(item: IMenuItem): boolean {
        const url = this.currentUrl() ?? '';
        if (item.route) {
            return url.startsWith(item.route);
        }
        if (item.subItems) {
            return item.subItems.some((sub) => this.isActive(sub));
        }
        return false;
    }

    toggleNestedMenu(event: Event): void {
        if (!this.item().subItems) {
            return;
        }
        event.stopPropagation();
        this.nestedMenuOpen.set(!this.nestedMenuOpen());
    }
}
